/**
 * Bulk Document Export Service
 * Exports variables, images, relationships, sections (blocks), and documents
 * from selected categories into the recommended ZIP folder structure.
 */

import { zipSync, strToU8 } from 'fflate';
import { IMAGE_ELEMENT } from './complexDocumentConstants';
import { VARIABLE_OBJECT_TYPES } from './constants/appConstants';
import { getDocumentsByCommunication } from './services/documentAPI';
import {
  getComplexDocument,
  templatesByGroup,
  getBlockByGroup,
} from './services/complexDocumentsAPI';
import { getAllBlockElements } from './services/elementAPI';
import { getAllVariables, getAllVariableRelations } from './services/variablesAPI';
import { getAllVariableCategory } from './services/variablesAPI';
import { getAllImages } from './services/imageAPI';

const DOCUMENTS_FOLDER = 'documents/';
const SECTIONS_FOLDER = 'sections/';

function sanitizeFolderName(name) {
  if (!name || typeof name !== 'string') return 'unnamed';
  return name.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').trim() || 'unnamed';
}

function collectVariableIdsFromContract(variableContract, outSet) {
  if (!variableContract || !Array.isArray(variableContract)) return;
  variableContract.forEach(item => {
    if (item.variableId) outSet.add(Number(item.variableId));
    if (VARIABLE_OBJECT_TYPES.includes(item.type) && item.content)
      collectVariableIdsFromContract(item.content, outSet);
  });
}

function collectVariableIdsFromContents(contents, outSet) {
  if (!contents || !Array.isArray(contents)) return;
  contents.forEach(c => {
    if (c.contentType === 'text' && c.contents) {
      c.contents.forEach(t => {
        if (t?.style?.isVariable && t.content) {
          const id = String(t.content).replace(/\{|\}/g, '');
          if (id && !isNaN(Number(id))) outSet.add(Number(id));
        }
      });
    }
    if (c.contentType === IMAGE_ELEMENT && c.style?.isVariable && c.content) {
      const id = String(c.content).replace(/\{|\}/g, '');
      if (id && !isNaN(Number(id))) outSet.add(Number(id));
    }
    if (c.contents) collectVariableIdsFromContents(c.contents, outSet);
  });
}

function collectImageUrlsFromContents(contents, outSet) {
  if (!contents || !Array.isArray(contents)) return;
  contents.forEach(c => {
    if (c.contentType === IMAGE_ELEMENT && c.url) outSet.add(c.url);
    if (c.contents) collectImageUrlsFromContents(c.contents, outSet);
  });
}

function walkBlock(block, onVariableContract, onContents) {
  if (!block) return;
  if (block.variableContract) onVariableContract(block.variableContract);
  if (block.contents) onContents(block.contents);
  (block.blockDTOList || []).forEach(child => walkBlock(child, onVariableContract, onContents));
}

function collectFromFlow(flow, variableIds, imageUrls) {
  const onContract = contract => collectVariableIdsFromContract(contract, variableIds);
  const onContents = contents => {
    collectVariableIdsFromContents(contents, variableIds);
    collectImageUrlsFromContents(contents, imageUrls);
  };
  (flow.blocks || []).forEach(b => walkBlock(b, onContract, onContents));
  if (flow.header) walkBlock(flow.header, onContract, onContents);
  if (flow.footer) walkBlock(flow.footer, onContract, onContents);
}

function collectFromDocumentDoc(docData, variableIds, imageUrls) {
  const data = docData?.data || docData;
  const flows = data?.flows || data?.documentDTO?.flows || [];
  flows.forEach(f => collectFromFlow(f, variableIds, imageUrls));
}

function collectFromBlockPayload(blockPayload, variableIds, imageUrls) {
  const onContract = contract => collectVariableIdsFromContract(contract, variableIds);
  const onContents = contents => {
    collectVariableIdsFromContents(contents, variableIds);
    collectImageUrlsFromContents(contents, imageUrls);
  };
  const blocks = blockPayload?.blocks ?? blockPayload?.blockDTOList ?? [];
  blocks.forEach(b => walkBlock(b, onContract, onContents));
}

export async function exportCategoryToZip(documentCategoryId, sectionCategoryIds, onProgress) {
  const sectionCategoryIdsArr = Array.isArray(sectionCategoryIds) ? sectionCategoryIds : [sectionCategoryIds].filter(Boolean);
  const variableIds = new Set();
  const imageUrls = new Set();

  if (onProgress) onProgress({ stage: 'fetching-documents', message: 'Fetching document list...' });

  const documentList = [];
  const limit = 50;
  let page = 1;
  let nextIndex = undefined;
  const qaStatus = false;

  while (true) {
    const payload = {
      page,
      limit,
      rgPermission: 'write',
      categoryId: documentCategoryId,
    };
    if (nextIndex != null) payload.index = nextIndex;

    const response = await getDocumentsByCommunication(qaStatus, payload);
    const ok = response && [3215, 3216].includes(response.statusCode) && Array.isArray(response.data);
    if (!ok) break;

    documentList.push(...response.data);
    const total = response.dataCount ?? documentList.length;
    if (onProgress) {
      onProgress({
        stage: 'fetching-documents',
        message: `Fetching document list...`,
        current: Math.min(documentList.length, total),
        total,
      });
    }
    const hasMore = response.hasMoreData !== false && response.data.length === limit;
    if (!hasMore) break;
    if (response.index != null) nextIndex = response.index;
    else page += 1;
  }

  // getDocumentsByCommunication returns communications with { communicationId, communicationName, categoryId, templates: [{ documentId, ... }] }; document ids are in templates[].documentId
  const documentListFlat = documentList.flatMap(item =>
    (item.templates || []).map(t => ({
      documentId: t.documentId,
      name: item.communicationName || t.name || `doc_${t.documentId || 'unknown'}`,
    })),
  ).filter(x => x.documentId);
  const docIds = documentListFlat.map(d => d.documentId);

  const documentGroups = new Map();
  const seenGroupIds = new Set();

  for (let i = 0; i < docIds.length; i++) {
    if (onProgress)
      onProgress({ stage: 'fetching-documents', current: i + 1, total: docIds.length, message: `Fetching document ${i + 1}/${docIds.length}` });
    try {
      const res = await getComplexDocument(docIds[i], undefined, false);
      const docData = res?.data || res;
      collectFromDocumentDoc(docData, variableIds, imageUrls);
      const groupId = docData?.groupId || docData?.data?.groupId;
      const name = docData?.name || docData?.data?.name || documentListFlat.find(d => d.documentId === docIds[i])?.name || `doc_${docIds[i]}`;
      const lang = docData?.language || docData?.data?.language || 'en';
      if (groupId && !seenGroupIds.has(groupId)) {
        seenGroupIds.add(groupId);
        try {
          const groupRes = await templatesByGroup(groupId);
          const groupList = groupRes?.data || groupRes;
          const list = Array.isArray(groupList) ? groupList : groupList ? [groupList] : [{ data: docData }];
          const docsByLang = {};
          for (const d of list) {
            const data = d?.data || d;
            const l = data?.language || 'en';
            const documentId = data?.documentId ?? data?.id;
            let fullData = data;
            if (documentId) {
              try {
                const fullRes = await getComplexDocument(documentId, undefined, false);
                fullData = fullRes?.data || fullRes;
              } catch (e) {
                console.warn('Failed to fetch document by id', documentId, e);
              }
            }
            docsByLang[l] = fullData;
            collectFromDocumentDoc(fullData, variableIds, imageUrls);
          }
          documentGroups.set(groupId, { name, docsByLang });
        } catch {
          documentGroups.set(`single_${docIds[i]}`, { name: sanitizeFolderName(name), docsByLang: { [lang]: docData } });
        }
      } else if (!groupId) {
        documentGroups.set(`single_${docIds[i]}`, { name: sanitizeFolderName(name), docsByLang: { [lang]: docData } });
      }
    } catch (e) {
      console.warn('Failed to fetch document', docIds[i], e);
    }
  }

  if (onProgress) onProgress({ stage: 'fetching-blocks', message: 'Fetching block list...' });

  let blockList = [];
  // getAllBlockElements in elementAPI uses limit: 8; use same value so we only treat a page as "short" when API returns fewer than 8
  const blockLimit = 8;
  for (let c = 0; c < sectionCategoryIdsArr.length; c++) {
    const categoryId = sectionCategoryIdsArr[c];
    let categoryBlocks = [];
    let blockPage = 1;
    let dataCount = null;
    while (true) {
      const res = await getAllBlockElements(blockPage, undefined, [categoryId], null, null, 'write', false);
      const data = res?.data || res;
      const list = Array.isArray(data) ? data : data?.data || [];
      if (dataCount == null && res?.dataCount != null) dataCount = res.dataCount;
      categoryBlocks = categoryBlocks.concat(list);
      if (list.length < blockLimit) break;
      if (dataCount != null && categoryBlocks.length >= dataCount) break;
      blockPage += 1;
    }
    blockList = blockList.concat(categoryBlocks);
  }

  const sectionGroups = new Map();
  const processedGroupIds = new Set();

  for (let i = 0; i < blockList.length; i++) {
    if (onProgress)
      onProgress({ stage: 'fetching-blocks', current: i + 1, total: blockList.length, message: `Fetching block ${i + 1}/${blockList.length}` });
    const block = blockList[i];
    const groupId = block.groupId || block.id;
    if (!groupId || processedGroupIds.has(groupId)) continue;
    processedGroupIds.add(groupId);
    try {
      const groupRes = await getBlockByGroup(groupId);
      const groupData = groupRes?.data || groupRes;
      const blocks = Array.isArray(groupData) ? groupData : groupData ? [groupData] : [];
      const byLang = {};
      for (const b of blocks) {
        const blockData = b?.data || b;
        const lang = blockData?.language || 'en';
        const blocksArr = blockData?.blocks ?? blockData?.blockDTOList ?? [];
        byLang[lang] = {
          flows: [{
            flowId: blockData?.parentBlockId,
            flowName: blockData?.parentBlockName,
            blocks: blocksArr,
            style: blockData?.style,
          }],
          style: blockData?.style,
        };
        collectFromBlockPayload(blockData, variableIds, imageUrls);
      }
      const blockName = blocks[0]?.parentBlockName || blocks[0]?.name || block?.parentBlockName || block?.name || `block_${groupId}`;
      sectionGroups.set(groupId, { name: sanitizeFolderName(blockName), byLang });
    } catch (e) {
      console.warn('Failed to fetch block group', groupId, e);
    }
  }

  if (onProgress) onProgress({ stage: 'fetching-resources', message: 'Fetching variables, images, relationships...' });

  const allVars = await getAllVariables({});
  const varList = Array.isArray(allVars) ? allVars : allVars?.data || [];
  const variablesExport = varList.filter(v => variableIds.has(Number(v.id))).map(v => ({
    id: v.id,
    name: v.name,
    displayName: v.displayName,
    placeHolderText: v.placeHolderText,
    variableCategoryName: v.variableCategoryName || v.variableCategory?.name,
    variableCategoryId: v.variableCategoryId,
    type: v.type,
    exampleValue: v.exampleValue,
    length: v.length,
    mandatory: v.mandatory,
    checkLength: v.checkLength,
    count: v.count,
  }));

  const categoryIdsUsed = new Set(variablesExport.map(v => v.variableCategoryId).filter(Boolean));
  const allRels = await getAllVariableRelations();
  const relList = Array.isArray(allRels) ? allRels : allRels?.data || [];
  const allCats = await getAllVariableCategory();
  const catData = allCats?.data || allCats || [];
  const catById = (catData || []).reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
  const relationshipsExport = relList
    .filter(r => categoryIdsUsed.has(r.sourceVariableCategoryId) && categoryIdsUsed.has(r.targetVariableCategoryId))
    .map(r => ({
      sourceCategoryName: (catById[r.sourceVariableCategoryId] || {}).name,
      targetCategoryName: (catById[r.targetVariableCategoryId] || {}).name,
      isArray: !!r.isArray,
    }))
    .filter(r => r.sourceCategoryName && r.targetCategoryName);

  const allImgs = await getAllImages({});
  const imgList = Array.isArray(allImgs) ? allImgs : allImgs?.data || [];
  const imagesExport = imgList.filter(img => {
    const url = img.imageLink || img.url;
    return url && imageUrls.has(url);
  }).map(img => ({
    id: img.id,
    name: img.name,
    path: img.path,
    imageLink: img.imageLink || img.url,
    imageAddress: img.imageAddress,
  }));

  if (onProgress) onProgress({ stage: 'building-zip', message: 'Building ZIP...' });

  const zipEntries = {};
  zipEntries['variables.json'] = strToU8(JSON.stringify(variablesExport, null, 2));
  zipEntries['images.json'] = strToU8(JSON.stringify(imagesExport, null, 2));
  zipEntries['relationships.json'] = strToU8(JSON.stringify(relationshipsExport, null, 2));

  const docFolderNames = new Set();
  documentGroups.forEach((val, key) => {
    let folderName = sanitizeFolderName(val.name);
    let suffix = 0;
    while (docFolderNames.has(folderName)) {
      suffix += 1;
      folderName = `${sanitizeFolderName(val.name)}_${String(suffix).padStart(2, '0')}`;
    }
    docFolderNames.add(folderName);
    Object.entries(val.docsByLang || {}).forEach(([lang, doc]) => {
      const payload = doc?.data || doc;
      const toWrite = {
        flows: payload?.flows || [],
        style: payload?.style || {},
        variableContract: payload?.variableContract || [],
        businessLogics: payload?.businessLogics,
        summary: payload?.summary,
      };
      zipEntries[`${DOCUMENTS_FOLDER}${folderName}/${lang}.json`] = strToU8(JSON.stringify(toWrite, null, 2));
    });
  });

  const sectionFolderNames = new Set();
  sectionGroups.forEach((val) => {
    let folderName = val.name;
    let suffix = 0;
    while (sectionFolderNames.has(folderName)) {
      suffix += 1;
      folderName = `${val.name}_${String(suffix).padStart(2, '0')}`;
    }
    sectionFolderNames.add(folderName);
    Object.entries(val.byLang || {}).forEach(([lang, data]) => {
      zipEntries[`${SECTIONS_FOLDER}${folderName}/${lang}.json`] = strToU8(JSON.stringify(data, null, 2));
    });
  });

  const zipBlob = zipSync(zipEntries, { level: 6 });
  return new Blob([zipBlob], { type: 'application/zip' });
}
